import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Grid,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faCircleMinus,
    faCircleXmark,
    faRotate,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { SettingsSection, settingsSectionPath, visibleSettingsSections } from './settingsSections';

/**
 * Zustand eines Bereichs, wie ihn `GET /Settings/Status` liefert.
 *
 * Von Hand deklariert, weil der generierte Client (`src/apiClient/Api.ts`) erst beim nächsten
 * `npm run generate-api` gegen einen laufenden Server aktualisiert wird. Danach kann hier auf
 * die generierten Typen umgestellt werden — die Feldnamen sind identisch.
 */
interface SettingsAreaStatus {
    key: string;
    enabled: boolean;
    configured: boolean;
    healthy: boolean | null;
    detail: string;
    elapsedMilliseconds: number | null;
}

interface SettingsStatus {
    checkedAt: string;
    areas: SettingsAreaStatus[];
}

/** Anzeigeform eines Zustands: Farbe, Symbol und Beschriftung gehören zusammen. */
type Badge = {
    color: 'success' | 'error' | 'warning' | 'default';
    icon: typeof faCircleCheck;
    labelKey: string;
};

/**
 * Übersetzt den Serverzustand in eine Anzeige.
 *
 * Die Reihenfolge der Fälle ist die Rangfolge der Ursachen: unvollständig konfiguriert ist ein
 * anderer Fehler als nicht erreichbar, und beides ist etwas anderes als bewusst abgeschaltet.
 */
function toBadge(status: SettingsAreaStatus | undefined): Badge | undefined {
    if (!status) return undefined;
    if (!status.enabled) {
        return { color: 'default', icon: faCircleMinus, labelKey: 'settings.status.disabled' };
    }
    if (!status.configured) {
        return { color: 'warning', icon: faTriangleExclamation, labelKey: 'settings.status.incomplete' };
    }
    if (status.healthy === true) {
        return { color: 'success', icon: faCircleCheck, labelKey: 'settings.status.healthy' };
    }
    if (status.healthy === false) {
        return { color: 'error', icon: faCircleXmark, labelKey: 'settings.status.unreachable' };
    }
    return { color: 'default', icon: faCircleMinus, labelKey: 'settings.status.unchecked' };
}

/**
 * Einstiegsseite der Verwaltung: eine Kachel je Bereich, mit Zustand dort, wo der Server einen
 * meldet. Damit sieht man ohne Klick durch alle Unterseiten, wo etwas hakt.
 */
export const SettingsOverview: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const context = useApplicationContext();

    const [status, setStatus] = useState<SettingsStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const sections = useMemo(
        () => visibleSettingsSections(context.userData!.userGroup!),
        [context.userData],
    );

    const load = useCallback(
        async (refresh: boolean) => {
            setLoading(true);
            setError(null);
            try {
                const response = await context.api.instance.get<SettingsStatus>('/Settings/Status', {
                    params: refresh ? { refresh: true } : undefined,
                });
                setStatus(response.data);
            } catch {
                // Die Übersicht bleibt ohne Zustände benutzbar — die Kacheln verlinken weiterhin.
                setError(t('settings.overview.statusFailed'));
            } finally {
                setLoading(false);
            }
        },
        [context.api, t],
    );

    useEffect(() => {
        void load(false);
    }, [load]);

    const statusByKey = useMemo(() => {
        const map = new Map<string, SettingsAreaStatus>();
        status?.areas.forEach((area) => map.set(area.key, area));
        return map;
    }, [status]);

    const renderCard = (section: SettingsSection) => {
        const path = settingsSectionPath(section);
        const badge = section.hasStatus ? toBadge(statusByKey.get(section.key)) : undefined;
        const detail = section.hasStatus ? statusByKey.get(section.key)?.detail : undefined;

        const content = (
            <CardContent sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ fontSize: '1.4rem', color: 'primary.main', mt: 0.25 }}>
                        <FontAwesomeIcon icon={section.icon} />
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
                        >
                            <Typography variant="subtitle1">{t(section.labelKey)}</Typography>
                            {badge && (
                                <Chip
                                    size="small"
                                    color={badge.color}
                                    variant={badge.color === 'default' ? 'outlined' : 'filled'}
                                    icon={<FontAwesomeIcon icon={badge.icon} style={{ fontSize: '0.8rem' }} />}
                                    label={t(badge.labelKey)}
                                />
                            )}
                            {!path && (
                                <Chip size="small" variant="outlined" label={t('settings.status.noPage')} />
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t(section.descriptionKey)}
                        </Typography>
                        {detail && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {detail}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        );

        return (
            <Grid key={section.key} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card sx={{ height: '100%' }}>
                    {path ? (
                        <CardActionArea onClick={() => navigate(path)} sx={{ height: '100%' }}>
                            {content}
                        </CardActionArea>
                    ) : (
                        content
                    )}
                </Card>
            </Grid>
        );
    };

    return (
        <Box>
            <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', rowGap: 1 }}
            >
                <Box>
                    <Typography variant="h6">{t('settings.overview.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {status
                            ? t('settings.overview.checkedAt', {
                                  time: new Date(status.checkedAt).toLocaleTimeString(),
                              })
                            : t('settings.overview.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    onClick={() => void load(true)}
                    startIcon={<FontAwesomeIcon icon={faRotate} />}
                >
                    {t('settings.overview.recheck')}
                </Button>
            </Stack>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={2}>
                {sections.map(renderCard)}
            </Grid>
        </Box>
    );
};

export default SettingsOverview;
